import { useTranslation } from 'react-i18next';
import { Icon } from '../Icon';

export interface OcsrSourceImageCardProps {
  readonly imageUrl: string;
  readonly imageName?: string | null | undefined;
  readonly expanded: boolean;
  readonly onToggleExpanded: () => void;
  readonly onDismiss: () => void;
}

/**
 * #1183 -- OCSR source image comparison card: displays the original source image
 * alongside the recognized structure on the drawing paper canvas so the user can
 * compare and correct any errors directly on the canvas.
 */
export function OcsrSourceImageCard({
  imageUrl,
  imageName,
  expanded,
  onToggleExpanded,
  onDismiss
}: OcsrSourceImageCardProps) {
  const { t } = useTranslation();
  const title = imageName ? `${t('ocsrSourceImageCard.title')}: ${imageName}` : t('ocsrSourceImageCard.title');

  return (
    <div
      className={`ocsr-source-image-card ${
        expanded ? 'ocsr-source-image-card--expanded' : 'ocsr-source-image-card--compact'
      }`}
      data-testid="ocsr-source-image-card"
    >
      <div className="ocsr-source-image-header">
        <span className="ocsr-source-image-title">
          <Icon name="image" size="1.1rem" />
          <span className="ocsr-source-image-label" title={title}>
            {title}
          </span>
        </span>
        <div className="ocsr-source-image-actions">
          <button
            type="button"
            className="icon-button ocsr-source-image-toggle"
            data-testid="ocsr-source-image-toggle"
            aria-label={t(expanded ? 'ocsrSourceImageCard.compact' : 'ocsrSourceImageCard.expand')}
            title={t(expanded ? 'ocsrSourceImageCard.compact' : 'ocsrSourceImageCard.expand')}
            onClick={onToggleExpanded}
          >
            <Icon name={expanded ? 'fitScreen' : 'zoomOutMap'} />
          </button>
          <button
            type="button"
            className="icon-button ocsr-source-image-dismiss"
            data-testid="ocsr-source-image-dismiss"
            aria-label={t('ocsrSourceImageCard.dismiss')}
            title={t('ocsrSourceImageCard.dismiss')}
            onClick={onDismiss}
          >
            <Icon name="close" />
          </button>
        </div>
      </div>
      <div className="ocsr-source-image-body">
        <img src={imageUrl} alt={imageName || t('ocsrSourceImageCard.title')} className="ocsr-source-image-img" />
      </div>
    </div>
  );
}
